import { styled } from '@mui/material/styles';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import LinkIcon from 'remixicon-react/LinkIcon';
import { VIEW_ARTICLE } from 'constants/routes';
import CloseCircleFillIcon from 'remixicon-react/CloseCircleFillIcon';
import { theme } from 'config/theme';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import SelectArticlesModal from '@buildwise/pages/project/SelectArticlesModal';
import { compact } from 'lodash';

type ArticleFormRelatedRecordsProps = {
  article?: ArticleType;
};

function ArticleFormRelatedRecords({ article }: ArticleFormRelatedRecordsProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
    getValues,
  } = useFormContext<PostFormData>();
  const relatedRecords = useWatch({ control, name: 'relatedRecords', defaultValue: [] });
  const [openLinkArticlesModal, setOpenLinkArticlesModal] = useState(false);
  const excludeArticleIds = useMemo(
    () => compact([article?.id, ...(relatedRecords?.map((record) => record.recordId) || [])]),
    [relatedRecords],
  );

  const handleRemoveArticle = useCallback(({ recordId }: { recordId: string }) => {
    const prevRelatedRecords = [...(getValues().relatedRecords || [])];
    const index = prevRelatedRecords.findIndex((record) => record.recordId === recordId);
    if (index > -1) {
      prevRelatedRecords.splice(index, 1);
      setValue('relatedRecords', prevRelatedRecords);
    }
  }, []);

  const handleLinkArticles = useCallback(
    ({ selectedArticles }: { selectedArticles: ArticleType[] }) => {
      const { relatedRecords: prevRelatedRecords = [] } = getValues();
      setValue('relatedRecords', [
        ...prevRelatedRecords,
        ...selectedArticles.map((item) => ({
          recordId: item.id,
          recordTitle: item.title,
          recordDescription: item.description,
          recordType: item.articleType || ArticleSourceType.POST,
          createdAt: item.createdAt,
          createdBy: item.createdBy,
          updatedBy:
            (item.activityLog?.reverse() || []).find((log) => log.type === 'Article Update')
              ?.userName || '',
          updatedAt: item.lastUpdatedAt || '',
        })),
      ]);
      setOpenLinkArticlesModal(false);
    },
    [],
  );

  return (
    <Box mb={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          {relatedRecords && relatedRecords?.length > 0 && (
            <Typography
              sx={(theme) => ({
                color: theme.palette.custom.tuna,
                fontSize: 16,
                fontWeight: 700,
                mb: 1,
              })}
            >
              Linked Posts
            </Typography>
          )}
        </Box>
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkIcon size={18} />}
            onClick={() => setOpenLinkArticlesModal(true)}
          >
            Link Post
          </Button>
        </Box>
      </Box>
      {relatedRecords?.map((record) => (
        <RecordContainer key={record.recordId}>
          <Box
            {...{
              component: Link,
              to: VIEW_ARTICLE.replace(':articleId', record.recordId),
              target: '_blank',
            }}
            sx={(theme) => ({
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              width: 'fit-content',
              gap: 1,
              color: theme.palette.custom.tuna,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: theme.palette.custom.link,
              },
            })}
          >
            <LinkIcon size={18} />
            <Typography
              sx={(theme) => ({
                color: 'inherit',
                fontSize: 14,
                fontWeight: 700,
              })}
            >
              {record.recordTitle}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleRemoveArticle({ recordId: record.recordId })}
          >
            <CloseCircleFillIcon size={18} color={theme.palette.custom.coolGrey} />
          </IconButton>
        </RecordContainer>
      ))}

      {openLinkArticlesModal && (
        <SelectArticlesModal
          title="Link Another Post"
          isSubmitting={isSubmitting}
          open={openLinkArticlesModal}
          setOpen={setOpenLinkArticlesModal}
          excludeArticleIds={excludeArticleIds}
          Actions={({ selectedArticles }) => (
            <>
              <Button
                onClick={() => handleLinkArticles({ selectedArticles })}
                variant="contained"
                disabled={selectedArticles.length === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Link'
                )}
              </Button>
            </>
          )}
        />
      )}
    </Box>
  );
}

export const RecordContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  backgroundColor: theme.palette.custom.blueChalk,
  borderRadius: 4,
  margin: '8px 0',
  padding: '4px 8px',
  minWidth: 400,
  width: 'fit-content',
}));

export default ArticleFormRelatedRecords;
